import React from 'react';
import { Alert, AlertTitle, Box, Link, Typography } from '@mui/material';

const alertListSx = {
  m: 0,
  pl: 2.5,
  '& > li + li': { mt: 0.75 },
};

type Props = {
  extensionInstalled: boolean;
};

type AutofillInfoAlertProps = Props & {
  extensionPurpose: string;
  howTitle: string;
  children: React.ReactNode;
  extraAlerts?: React.ReactNode;
};

const AutofillInfoAlert = ({
  extensionInstalled,
  extensionPurpose,
  howTitle,
  children,
  extraAlerts,
}: AutofillInfoAlertProps) => (
  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {!extensionInstalled ? (
      <Alert severity="warning">
        <AlertTitle>Extension required</AlertTitle>
        Install and enable the Harbor browser extension to {extensionPurpose}.
      </Alert>
    ) : null}

    {extraAlerts}

    <Alert severity="info">
      <AlertTitle>{howTitle}</AlertTitle>
      <Typography component="ul" variant="body2" sx={alertListSx}>
        {children}
      </Typography>
    </Alert>
  </Box>
);

export const LinkedInAutofillInfoAlert = ({ extensionInstalled }: Props) => (
  <AutofillInfoAlert
    extensionInstalled={extensionInstalled}
    extensionPurpose="search LinkedIn locations/skills and to autofill job posts"
    howTitle="How Job Autofill for LinkedIn works"
    extraAlerts={
      <Alert severity="warning">
        <AlertTitle>Job location</AlertTitle>
        Pick the location from LinkedIn&apos;s dropdown after Autofill.
      </Alert>
    }
  >
    <li>
      To use Autofill, navigate to a single job on Harbor and press &quot;Post to job boards&quot;, then press
      &quot;Post job to LinkedIn&quot;. Review the Autofill fields, press &quot;Looks good&quot;, then &quot;Go to
      LinkedIn&quot;. You will be redirected to &quot;Post a Job&quot; form at the{' '}
      <Link href="https://www.linkedin.com/talent/job-posting/post" target="_blank" rel="noopener noreferrer">
        LinkedIn Recruiter
      </Link>{' '}
      website. Harbor&apos;s browser extension will autofill the form with no additional action from you.
    </li>
    <li>
      Alternatively, navigate to{' '}
      <Link href="https://www.linkedin.com/talent/job-posting/post" target="_blank" rel="noopener noreferrer">
        &quot;Post a Job&quot;
      </Link>{' '}
      at the LinkedIn Recruiter website, then click on Harbor&apos;s browser extension icon to open it. Select a
      job to use to autofill the form.
    </li>
  </AutofillInfoAlert>
);

export const ZipRecruiterAutofillInfoAlert = ({ extensionInstalled }: Props) => (
  <AutofillInfoAlert
    extensionInstalled={extensionInstalled}
    extensionPurpose="search ZipRecruiter locations and to autofill job posts"
    howTitle="How Job Autofill for ZipRecruiter works"
  >
    <li>
      To use Autofill, navigate to a single job on Harbor and press &quot;Post to job boards&quot;, then press
      &quot;Post job to ZipRecruiter&quot;. Review the Autofill fields, press &quot;Looks good&quot;, then
      &quot;Go to ZipRecruiter&quot;. You will be redirected to the job create form at the{' '}
      <Link href="https://www.ziprecruiter.com/emp/job/create" target="_blank" rel="noopener noreferrer">
        ZipRecruiter
      </Link>{' '}
      website. Harbor&apos;s browser extension will autofill the form with no additional action from you.
    </li>
    <li>
      Alternatively, navigate to the{' '}
      <Link href="https://www.ziprecruiter.com/emp/job/create" target="_blank" rel="noopener noreferrer">
        job create form
      </Link>{' '}
      at the ZipRecruiter website, then click on Harbor&apos;s browser extension icon to open it. Select a job to
      use to autofill the form.
    </li>
  </AutofillInfoAlert>
);

export const IndeedAutofillInfoAlert = ({ extensionInstalled }: Props) => (
  <AutofillInfoAlert
    extensionInstalled={extensionInstalled}
    extensionPurpose="search Indeed locations and to autofill job posts"
    howTitle="How Job Autofill for Indeed works"
  >
    <li>
      To use Autofill, navigate to a single job on Harbor and press &quot;Post to job boards&quot;, then press
      &quot;Post job to Indeed&quot;. Review the Autofill fields, press &quot;Looks good&quot;, then &quot;Go to
      Indeed&quot;. You will be redirected to the job posting form at the{' '}
      <Link
        href="https://employers.indeed.com/job-posting/from-scratch/getting-started"
        target="_blank"
        rel="noopener noreferrer"
      >
        Indeed
      </Link>{' '}
      website. Harbor&apos;s browser extension will autofill the form with no additional action from you.
    </li>
    <li>
      Alternatively, navigate to the{' '}
      <Link
        href="https://employers.indeed.com/job-posting/from-scratch/getting-started"
        target="_blank"
        rel="noopener noreferrer"
      >
        job posting form
      </Link>{' '}
      at the Indeed website, then click on Harbor&apos;s browser extension icon to open it. Select a job to use to
      autofill the form.
    </li>
  </AutofillInfoAlert>
);
